/*
 * Voidwear sizing chart unit toggle.
 *
 * Reads localStorage 'voidwear:sizingUnit' if present, otherwise uses the
 * server-rendered default (set via data-default-unit by the Liquid block,
 * which considers the customer's country). Re-renders the table cells and
 * tolerance footnote from their data-vw-cm source values when the user
 * toggles between cm and in.
 *
 * The block may appear multiple times on a page (multiple products on a
 * collection preview, etc.), so this script binds to each instance and
 * keeps them in sync via a shared preference key.
 */

const STORAGE_KEY = 'voidwear:sizingUnit';
const VALID_UNITS = new Set(['cm', 'in']);
const CM_PER_IN = 2.54;

function getStoredUnit() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return VALID_UNITS.has(stored) ? stored : null;
  } catch (_) {
    return null;
  }
}

function setStoredUnit(unit) {
  try {
    localStorage.setItem(STORAGE_KEY, unit);
  } catch (_) {
    // Storage may be unavailable in private browsing, etc. Fail silently.
  }
}

function formatValue(cm, unit) {
  if (unit === 'in') {
    return Math.round(cm / CM_PER_IN).toString();
  }
  return Math.round(cm).toString();
}

function formatTolerance(cm, unit) {
  if (unit === 'in') {
    const inches = cm / CM_PER_IN;
    // 1 decimal place for tolerance since it's often a non-whole value.
    return `${(Math.round(inches * 10) / 10).toFixed(1)} in`;
  }
  return `${Math.round(cm)} cm`;
}

function applyUnit(block, unit) {
  if (!VALID_UNITS.has(unit)) return;

  // Update toggle button pressed state
  block.querySelectorAll('.vw-sizing-chart__unit-btn').forEach((btn) => {
    btn.setAttribute('aria-pressed', btn.dataset.vwUnit === unit ? 'true' : 'false');
  });

  // Update header unit labels (CM / IN inside the parentheses)
  block.querySelectorAll('[data-vw-unit-display]').forEach((el) => {
    el.textContent = unit;
  });

  // Update each cell value from its stored cm
  block.querySelectorAll('[data-vw-cm]').forEach((cell) => {
    const cm = parseFloat(cell.dataset.vwCm);
    if (!Number.isNaN(cm)) {
      cell.textContent = formatValue(cm, unit);
    }
  });

  // Update tolerance footnote
  const toleranceEl = block.querySelector('[data-vw-tolerance-cm]');
  if (toleranceEl) {
    const cm = parseFloat(toleranceEl.dataset.vwToleranceCm);
    if (!Number.isNaN(cm)) {
      toleranceEl.innerHTML = `&plusmn; ${formatTolerance(cm, unit)}`;
    }
  }
}

function initBlock(block) {
  const defaultUnit = block.dataset.defaultUnit || 'cm';
  const initialUnit = getStoredUnit() || defaultUnit;

  applyUnit(block, initialUnit);

  block.querySelectorAll('.vw-sizing-chart__unit-btn').forEach((btn) => {
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      const unit = btn.dataset.vwUnit;
      if (!VALID_UNITS.has(unit)) return;
      setStoredUnit(unit);
      // Apply to all blocks on the page so they stay in sync
      document.querySelectorAll('.vw-sizing-chart').forEach((b) => applyUnit(b, unit));
    });
  });
}

function init() {
  document.querySelectorAll('.vw-sizing-chart').forEach(initBlock);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}